import {
  modeOklch,
  modeRgb,
  modeLrgb,
  converter,
  useMode as culoriUseMode,
  serializeHex,
  wcagContrast,
  interpolate
} from 'culori/fn'

import { isDarkHexBackground } from './is_dark_background'

/**
 * OKLCH wash amounts for a bright primary on a dark embed background. Low blends stay
 * indistinguishable from the page; these targets keep bookable circles visible while
 * preserving primary-colored day text contrast.
 */
const DARK_EMBED_BRIGHT_PRIMARY_WASH = {
  secondary: 0.40,
  secondaryHover: 0.45,
  secondaryActive: 0.55,
  tertiary: 0.50,
  tertiaryHover: 0.52
}

/** Light fill for selected spot time when inverse text is dark on a dark embed. */
const DARK_EMBED_SELECTED_TIME_OVERLAY = {
  lightness: 0.89,
  chromaScale: 0.35,
  chromaMax: 0.06
}

function clampChroma(c) {
  return Math.max(0, Math.min(0.4, c))
}

function actionSecondaryWash(backgroundColor, buttonColor, amount) {
  return interpolate([backgroundColor, buttonColor], 'oklch')(amount)
}

/**
 * Light action-surface tints for embed palettes. Matches design-system intent
 * (e.g. coreColorBlue025/100): pale washes behind primary-colored text, not
 * slightly shifted primary shades.
 */
function lightActionSurfaceTint(oklch, buttonColor, { lightness, chromaScale, chromaMax }) {
  const primary = oklch(buttonColor)
  const tint = oklch(buttonColor)
  tint.l = lightness
  tint.c = clampChroma(Math.min(primary.c * chromaScale, chromaMax))
  return tint
}

/** Matches colorTextInverseStandard: white-on-primary below 4.5:1 yields dark inverse text. */
function primaryNeedsDarkInverseText(oklch, buttonColor) {
  return wcagContrast(oklch('#ffffff'), oklch(buttonColor)) < 4.5
}

/** Invitee form controls stay on a white surface even when the embed page is dark. */
const FIELD_SURFACE = '#ffffff'

/** Calendly default body text on light surfaces (initTheme TEXT_COLOR_DEFAULT). */
const DEFAULT_FIELD_TEXT = '#0a2540'

/** CUI destructive red; 4.5:1 on white so field errors stay readable. */
const DEFAULT_FIELD_DESTRUCTIVE = '#de1f1f'

const FIELD_TEXT_CONTRAST = 4.5
const FIELD_DISABLED_CONTRAST = 3

function textReadableOnFieldSurface(oklch, textColor) {
  const normalizedText = textColor.startsWith('#') ? textColor : `#${textColor}`
  if (wcagContrast(oklch(normalizedText), oklch(FIELD_SURFACE)) >= FIELD_TEXT_CONTRAST) {
    return oklch(normalizedText)
  }
  return oklch(DEFAULT_FIELD_TEXT)
}

function meetContrastOnFieldSurface(oklch, rgbConverter, color, minContrast, fallbackHex) {
  const surface = oklch(FIELD_SURFACE)
  let candidate = oklch(serializeHex(rgbConverter(color)))
  if (wcagContrast(candidate, surface) >= minContrast) {
    return candidate
  }

  for (let step = 0; step < 20; step += 1) {
    candidate.l = Math.max(0, candidate.l - 0.05)
    const next = oklch(serializeHex(rgbConverter(candidate)))
    if (wcagContrast(next, surface) >= minContrast) {
      return next
    }
    candidate = next
  }

  return oklch(fallbackHex)
}

function deriveFieldTextTokens(oklch, rgbConverter, textColor) {
  const colorTextFieldStandard = textReadableOnFieldSurface(oklch, textColor)

  const derivedSecondary = oklch(serializeHex(rgbConverter(colorTextFieldStandard)))
  derivedSecondary.l += derivedSecondary.l > 0.5 ? -0.25 : 0.25
  const colorTextFieldSecondary = meetContrastOnFieldSurface(
    oklch,
    rgbConverter,
    derivedSecondary,
    FIELD_TEXT_CONTRAST,
    DEFAULT_FIELD_TEXT
  )

  const derivedDisabled = oklch(serializeHex(rgbConverter(colorTextFieldSecondary)))
  derivedDisabled.c = clampChroma(derivedDisabled.c - 0.2)
  derivedDisabled.l += derivedDisabled.l > 0.5 ? -0.1 : 0.2
  const colorTextFieldDisabled = meetContrastOnFieldSurface(
    oklch,
    rgbConverter,
    derivedDisabled,
    FIELD_DISABLED_CONTRAST,
    DEFAULT_FIELD_TEXT
  )

  return { colorTextFieldStandard, colorTextFieldSecondary, colorTextFieldDisabled }
}

function deriveActionSecondarySurfaces(oklch, backgroundColor, buttonColor) {
  const darkEmbed = isDarkHexBackground(backgroundColor.replace('#', ''))
  const brightPrimary = primaryNeedsDarkInverseText(oklch, buttonColor)
  const useBackgroundWash = darkEmbed && brightPrimary

  if (useBackgroundWash) {
    const { secondary, secondaryHover, secondaryActive, tertiary, tertiaryHover } = DARK_EMBED_BRIGHT_PRIMARY_WASH

    return {
      colorSurfaceActionSecondary: actionSecondaryWash(backgroundColor, buttonColor, secondary),
      colorSurfaceActionSecondaryHover: actionSecondaryWash(backgroundColor, buttonColor, secondaryHover),
      colorSurfaceActionSecondaryActive: actionSecondaryWash(backgroundColor, buttonColor, secondaryActive),
      colorSurfaceActionTertiary: actionSecondaryWash(backgroundColor, buttonColor, tertiary),
      colorSurfaceActionTertiaryHover: actionSecondaryWash(backgroundColor, buttonColor, tertiaryHover),
      // Selected spot time uses inverse (dark) text on overlay-medium, not primary-colored text.
      colorBackgroundOverlayMedium: lightActionSurfaceTint(oklch, buttonColor, DARK_EMBED_SELECTED_TIME_OVERLAY)
    }
  }

  return {
    colorSurfaceActionSecondary: lightActionSurfaceTint(oklch, buttonColor, {
      lightness: 0.97,
      chromaScale: 0.08,
      chromaMax: 0.04
    }),
    colorSurfaceActionSecondaryHover: lightActionSurfaceTint(oklch, buttonColor, {
      lightness: 0.89,
      chromaScale: 0.35,
      chromaMax: 0.06
    }),
    colorSurfaceActionSecondaryActive: lightActionSurfaceTint(oklch, buttonColor, {
      lightness: 0.82,
      chromaScale: 0.55,
      chromaMax: 0.10
    }),
    colorSurfaceActionTertiary: lightActionSurfaceTint(oklch, buttonColor, {
      lightness: 0.924,
      chromaScale: 0.25,
      chromaMax: 0.05
    }),
    colorSurfaceActionTertiaryHover: lightActionSurfaceTint(oklch, buttonColor, {
      lightness: 0.89,
      chromaScale: 0.35,
      chromaMax: 0.055
    })
  }
}

export function generatePaletteCulori({ backgroundColor, textColor, buttonColor }) {
  const oklch = culoriUseMode(modeOklch)
  culoriUseMode(modeRgb)
  culoriUseMode(modeLrgb)

  const rgbConverter = converter('rgb')

  // COLOR TEXT

  const colorTextStandard = oklch(textColor)

  const colorTextSecondary = oklch(textColor)
  colorTextSecondary.l += colorTextSecondary.l > 0.5 ? -0.25 : 0.25

  const colorTextDisabled = oklch(colorTextSecondary)
  colorTextDisabled.c = clampChroma(colorTextDisabled.c - 0.2)
  colorTextDisabled.l += colorTextDisabled.l > 0.5 ? -0.1 : 0.2

  // COLOR BACKGROUND

  const colorBackgroundStandard = oklch(backgroundColor)

  const colorBackgroundSecondary = oklch(backgroundColor)
  colorBackgroundSecondary.l += colorBackgroundSecondary.l < 0.75 ? 0.075 : -0.075

  const colorBackgroundTertiary = oklch(backgroundColor)
  colorBackgroundTertiary.l += colorBackgroundTertiary.l < 0.75 ? 0.125 : -0.125

  // COLOR BACKGROUND INVERSE

  const colorBackgroundInverseStandard = oklch(backgroundColor)
  colorBackgroundInverseStandard.c = colorBackgroundInverseStandard.l >= 0.2 ? 0.01 : 0.4
  colorBackgroundInverseStandard.l = colorBackgroundInverseStandard.l < 0.5
    ? 1 - (colorBackgroundInverseStandard.l / 1000)
    : colorBackgroundInverseStandard.l / 1000

  const colorBackgroundInverseSecondary = oklch(colorBackgroundInverseStandard)
  colorBackgroundInverseSecondary.l = colorBackgroundInverseSecondary.l < 0.5 ? 0.3 : -0.1

  const colorBackgroundInverseTertiary = oklch(colorBackgroundInverseStandard)
  colorBackgroundInverseTertiary.l += colorBackgroundInverseTertiary.l < 0.5 ? 0.4 : -0.2

  // COLOR TEXT INVERSE

  const colorTextInverseStandard = oklch('#ffffff')
  if (wcagContrast(colorTextInverseStandard, buttonColor) < 4.5) {
    colorTextInverseStandard.l = 0
  }

  const colorTextInverseSecondary = oklch(colorTextInverseStandard)
  colorTextInverseSecondary.l += colorTextInverseSecondary.l <= 0.9 ? -0.15 : 0.15
  colorTextInverseSecondary.l += colorBackgroundInverseSecondary.l > 0.55 ? 0.2 : -0.1

  const colorTextInverseDisabled = oklch(colorTextInverseSecondary)
  colorTextInverseDisabled.c = clampChroma(colorTextInverseDisabled.c - 0.3)
  colorTextInverseDisabled.l += colorBackgroundTertiary.l > 0.6 ? -0.1 : 0.1

  const colorTextInverseAction = oklch('#ffffff')
  colorTextInverseAction.l = oklch(buttonColor).l >= 0.6 ? 0.1 : 0.9

  const colorTextActionContrast = oklch(buttonColor)
  colorTextActionContrast.l += colorTextActionContrast.l > 0.5 ? -0.1 : 0.1

  // COLOR SURFACE ACTION
  const colorSurfaceAction = oklch(buttonColor)

  const colorSurfaceActionHover = oklch(buttonColor)
  colorSurfaceActionHover.l -= 0.05

  const colorSurfaceActionActive = oklch(buttonColor)
  colorSurfaceActionActive.l -= 0.1

  const {
    colorSurfaceActionSecondary,
    colorSurfaceActionSecondaryHover,
    colorSurfaceActionSecondaryActive,
    colorSurfaceActionTertiary,
    colorSurfaceActionTertiaryHover,
    colorBackgroundOverlayMedium
  } = deriveActionSecondarySurfaces(oklch, backgroundColor, buttonColor)

  // COLOR ICON
  const colorIconHover = oklch(colorTextStandard)
  colorIconHover.l += colorIconHover.l > 0.5 ? -0.1 : 0.1

  const {
    colorTextFieldStandard,
    colorTextFieldSecondary,
    colorTextFieldDisabled
  } = deriveFieldTextTokens(oklch, rgbConverter, textColor)

  return {
    colorTextStandard: serializeHex(rgbConverter(colorTextStandard)),
    colorTextFieldStandard: serializeHex(rgbConverter(colorTextFieldStandard)),
    colorTextFieldSecondary: serializeHex(rgbConverter(colorTextFieldSecondary)),
    colorTextFieldDisabled: serializeHex(rgbConverter(colorTextFieldDisabled)),
    colorTextFieldDestructive: DEFAULT_FIELD_DESTRUCTIVE,
    colorTextSecondary: serializeHex(rgbConverter(colorTextSecondary)),
    colorTextDisabled: serializeHex(rgbConverter(colorTextDisabled)),
    colorTextAction: buttonColor,
    colorTextActionContrast: serializeHex(rgbConverter(colorTextActionContrast)),
    colorTextInverseStandard: serializeHex(rgbConverter(colorTextInverseStandard)),
    colorTextInverseSecondary: serializeHex(rgbConverter(colorTextInverseSecondary)),
    colorTextInverseDisabled: serializeHex(rgbConverter(colorTextInverseDisabled)),
    colorTextInverseAction: serializeHex(rgbConverter(colorTextInverseAction)),
    colorTextInverseActionContrast: '',

    colorBackgroundInverseStandard: serializeHex(rgbConverter(colorBackgroundInverseStandard)),
    colorBackgroundInverseSecondary: serializeHex(rgbConverter(colorBackgroundInverseSecondary)),
    colorBackgroundInverseTertiary: serializeHex(rgbConverter(colorBackgroundInverseTertiary)),

    colorBackgroundStandard: serializeHex(rgbConverter(colorBackgroundStandard)),
    colorBackgroundSecondary: serializeHex(rgbConverter(colorBackgroundSecondary)),
    colorBackgroundTertiary: serializeHex(rgbConverter(colorBackgroundTertiary)),
    ...(colorBackgroundOverlayMedium && {
      colorBackgroundOverlayMedium: serializeHex(rgbConverter(colorBackgroundOverlayMedium))
    }),

    colorSurfaceAction: serializeHex(rgbConverter(colorSurfaceAction)),
    colorSurfaceActionHover: serializeHex(rgbConverter(colorSurfaceActionHover)),
    colorSurfaceActionActive: serializeHex(rgbConverter(colorSurfaceActionActive)),
    colorSurfaceActionSecondary: serializeHex(rgbConverter(colorSurfaceActionSecondary)),
    colorSurfaceActionSecondaryHover: serializeHex(rgbConverter(colorSurfaceActionSecondaryHover)),
    colorSurfaceActionSecondaryActive: serializeHex(rgbConverter(colorSurfaceActionSecondaryActive)),
    colorSurfaceActionTertiary: serializeHex(rgbConverter(colorSurfaceActionTertiary)),
    colorSurfaceActionTertiaryHover: serializeHex(rgbConverter(colorSurfaceActionTertiaryHover)),

    colorSurfaceInverseStandard: backgroundColor,
    colorSurfaceInverseSecondary: serializeHex(rgbConverter(colorBackgroundSecondary)),
    colorSurfaceInverseDisabled: serializeHex(rgbConverter(colorBackgroundTertiary)),

    colorStrokeFocus: buttonColor,

    colorIconStandard: serializeHex(rgbConverter(colorTextStandard)),
    colorIconHover: serializeHex(rgbConverter(colorIconHover)),
    colorIconSecondary: serializeHex(rgbConverter(colorTextSecondary)),
    colorIconActive: buttonColor,
    colorIconDisabled: serializeHex(rgbConverter(colorTextDisabled)),
    colorIconInverseStandard: serializeHex(rgbConverter(colorTextInverseStandard)),
    colorIconInverseHover: '',
    colorIconInverseSecondary: serializeHex(rgbConverter(colorTextInverseSecondary)),
    colorIconInverseActive: serializeHex(rgbConverter(colorTextInverseAction)),
    colorIconInverseDisabled: serializeHex(rgbConverter(colorTextInverseDisabled))
  }
}
